<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  step: {
    type: Object,
    default: null,
  },
  nextOrder: {
    type: Number,
    default: 1,
  },
});

const emit = defineEmits(['save']);

const { t } = useI18n();
const dialogRef = ref(null);
const isSubmitting = ref(false);

const formState = ref({
  name: '',
  messageType: 'text',
  content: '',
  mediaUrl: '',
  delayDays: 0,
  delayHours: 0,
  delayMinutes: 0,
  active: true,
});

const errors = ref({});

const isEditing = computed(() => !!props.step?.id);

const dialogTitle = computed(() =>
  isEditing.value
    ? t('CAMPAIGN.FOLLOW_UP.STEP.EDIT')
    : t('CAMPAIGN.FOLLOW_UP.STEP.CREATE')
);

const messageTypeOptions = [
  { value: 'text', label: 'Text' },
  { value: 'image', label: 'Image' },
  { value: 'video', label: 'Video' },
  { value: 'audio', label: 'Audio' },
  { value: 'document', label: 'Document' },
  { value: 'template', label: 'Template' },
];

const needsMediaUrl = computed(() =>
  ['image', 'video', 'audio', 'document'].includes(formState.value.messageType)
);

watch(
  () => props.step,
  newStep => {
    if (newStep) {
      formState.value = {
        name: newStep.name || '',
        messageType: newStep.message_type || 'text',
        content: newStep.content || '',
        mediaUrl: newStep.media_url || '',
        delayDays: newStep.delay_days || 0,
        delayHours: newStep.delay_hours || 0,
        delayMinutes: newStep.delay_minutes || 0,
        active: newStep.active ?? true,
      };
    }
  },
  { immediate: true }
);

const resetForm = () => {
  formState.value = {
    name: '',
    messageType: 'text',
    content: '',
    mediaUrl: '',
    delayDays: 0,
    delayHours: 0,
    delayMinutes: 0,
    active: true,
  };
  errors.value = {};
};

const validate = () => {
  errors.value = {};

  if (
    formState.value.messageType === 'text' &&
    !formState.value.content.trim()
  ) {
    errors.value.content = t('CAMPAIGN.FOLLOW_UP.STEP.CONTENT_REQUIRED');
  }

  if (needsMediaUrl.value && !formState.value.mediaUrl.trim()) {
    errors.value.mediaUrl = t('CAMPAIGN.FOLLOW_UP.STEP.MEDIA_REQUIRED');
  }

  return Object.keys(errors.value).length === 0;
};

const open = () => {
  if (!props.step) resetForm();
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const handleSubmit = () => {
  if (!validate()) return;

  isSubmitting.value = true;
  try {
    const payload = {
      name: formState.value.name || null,
      message_type: formState.value.messageType,
      content: formState.value.content,
      media_url: needsMediaUrl.value ? formState.value.mediaUrl : null,
      delay_days: Number(formState.value.delayDays) || 0,
      delay_hours: Number(formState.value.delayHours) || 0,
      delay_minutes: Number(formState.value.delayMinutes) || 0,
      active: formState.value.active,
      step_order: props.step?.step_order || props.nextOrder,
    };

    if (isEditing.value) {
      payload.id = props.step.id;
    }

    emit('save', payload);
    close();
  } finally {
    isSubmitting.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="dialogTitle"
    width="xl"
    :show-confirm-button="false"
    :show-cancel-button="false"
  >
    <form class="space-y-4" @submit.prevent="handleSubmit">
      <Input
        v-model="formState.name"
        :label="t('CAMPAIGN.FOLLOW_UP.STEP.LABEL_NAME')"
        :placeholder="t('CAMPAIGN.FOLLOW_UP.STEP.LABEL_NAME_PLACEHOLDER')"
      />

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.FOLLOW_UP.STEP.LABEL_TYPE') }}
        </label>
        <ComboBox
          v-model="formState.messageType"
          :options="messageTypeOptions"
          class="[&>div>button]:bg-n-alpha-black2"
        />
      </div>

      <TextArea
        v-model="formState.content"
        :label="t('CAMPAIGN.FOLLOW_UP.STEP.LABEL_CONTENT')"
        :placeholder="t('CAMPAIGN.FOLLOW_UP.STEP.LABEL_CONTENT_PLACEHOLDER')"
        rows="4"
        :message="errors.content || ''"
        :message-type="errors.content ? 'error' : 'info'"
      />

      <Input
        v-if="needsMediaUrl"
        v-model="formState.mediaUrl"
        :label="t('CAMPAIGN.FOLLOW_UP.STEP.LABEL_MEDIA_URL')"
        :placeholder="t('CAMPAIGN.FOLLOW_UP.STEP.LABEL_MEDIA_PLACEHOLDER')"
        :message="errors.mediaUrl || ''"
        :message-type="errors.mediaUrl ? 'error' : 'info'"
      />

      <div>
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.FOLLOW_UP.STEP.LABEL_DELAY') }}
        </label>
        <div class="mt-2 flex gap-3">
          <div class="flex-1">
            <Input
              v-model.number="formState.delayDays"
              :label="t('CAMPAIGN.FOLLOW_UP.STEP.LABEL_DAYS')"
              type="number"
              min="0"
            />
          </div>
          <div class="flex-1">
            <Input
              v-model.number="formState.delayHours"
              :label="t('CAMPAIGN.FOLLOW_UP.STEP.LABEL_HOURS')"
              type="number"
              min="0"
              max="23"
            />
          </div>
          <div class="flex-1">
            <Input
              v-model.number="formState.delayMinutes"
              :label="t('CAMPAIGN.FOLLOW_UP.STEP.LABEL_MINUTES')"
              type="number"
              min="0"
              max="59"
            />
          </div>
        </div>
      </div>

      <div class="flex items-center justify-between">
        <div>
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.FOLLOW_UP.STEP.LABEL_ACTIVE') }}
          </span>
          <p class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN.FOLLOW_UP.STEP.LABEL_ACTIVE_DESC') }}
          </p>
        </div>
        <Switch v-model="formState.active" />
      </div>

      <div class="flex justify-end gap-3 pt-4 border-t border-n-weak">
        <Button type="button" variant="faded" color="slate" @click="close">
          {{ t('CAMPAIGN.FOLLOW_UP.STEP.CANCEL') }}
        </Button>
        <Button type="submit" :is-loading="isSubmitting">
          {{ t('CAMPAIGN.FOLLOW_UP.STEP.SAVE') }}
        </Button>
      </div>
    </form>
  </Dialog>
</template>
